<template>
    <f7-page
        ref="self"
        :page-content="false"
        class="login-screen-page"
        @page:beforein="PAGE_BEFORE_IN"
        @page:afterin="PAGE_AFTER_IN"
    >
        <f7-navbar class="login-screen-navbar">
            <f7-nav-left @click="openLanguagePopup">
                <div class="text-wrapper">
                    <p>{{ detectCountryName(lang) }}</p>
                    <i class="icon i-small-arrow-down"></i>
                </div>
            </f7-nav-left>
            <f7-nav-title @click="techController.openTechPage()">
                <i class="icon sloth-logo-m-svg"/>
                <i class="icon logo-small-svg"/>
            </f7-nav-title>
            <f7-nav-right @click="openAlertToTelegram">
                <i class="icon telegram-svg"></i>
            </f7-nav-right>
        </f7-navbar>
        <f7-page-content>
            <f7-block>
                <div class="top-content">
                    <PageHeadline :title="headline.title" :text="headline.text"/>
                    <div class="phone-form" :class="{'error': error}">
                        <phoneNumberInput
                            v-model="tel"
                            show-code-on-list
                            :error="error"
                            ref="PhoneNumberInputRef"
                            @update="updatePhoneValue"
                            @update-error="error = $event"
                            :success="result?.isValid"
                        />
                        <f7-button
                            @click="handleClickPhoneForm"
                            class="theme-type"
                            :loading="submitState.loading"
                            preloader
                        >{{ $t("g.login.step1.submit") }}
                        </f7-button>
                    </div>
                    <div id="login-page_cf-turnstile"></div>
                </div>

                <div class="login-screen-page_bottom-block">
                    <version
                        ref="versionRef"
                        :releazioConfig="serviceRealizio?.activeConfig.value"
                        :currentVersion="currentVersion"
                        :isNeedUpdate="serviceRealizio?.isNeedUpdate"
                        :isViewLastPost="serviceRealizio?.isViewedLastPost.value || !serviceRealizio.hasVersionPost()"
                        :isCanShowUpdateButton="isCordova"
                        @openDeeplink="openReleazioDeeplink"
                        @openPosts="toReleazioPostsPage"
                    />
                </div>
            </f7-block>
        </f7-page-content>
        <LanguagesPopup ref="languagesPopup" @close="close"/>
    </f7-page>
</template>

<script setup lang="ts">
import LanguagesPopup from "@/targets/main/views/login/components/LanguagesPopup.vue";
import {useI18n} from 'vue-i18n';
import LoginController, {Channel} from "./LoginController";
import {f7} from 'framework7-vue';
import {computed, ref} from "@vue/runtime-core";
import PageHeadline from '@/components/atomic/page-headline/PageHeadline.vue';
import LoginEnterPasswordPage from './subpages/LoginEnterPasswordPage.vue';
import LoginEnterCodePage from './subpages/LoginEnterCodePage.vue';
import {getDevice} from "framework7/shared/get-device";
import 'maz-ui/css/main.css'
import phoneNumberInput from "@/components/maz/components/MazPhoneNumberInput.vue";
import {nextTick, onMounted, onUnmounted, reactive, watch} from "vue";
import LangService from "@/services/LangService";
import StatusBarService from "@/services/StatusBarService";
import {CaptchaActions} from "@/targets/main/views/login/types/CaptchaActions";
import {CaptchaPlace} from "@/targets/main/views/login/types/CaptchaPlace";
import TelegramPage from "@/targets/main/views/login/subpages/TelegramPage.vue";
import ViewTechController from "@/views/v2/view-tech/ViewTechController";
import * as Sentry from "@sentry/vue";
// @ts-ignore
import AppController from "@target/components/App/ts/AppController";
import Version from "@components/atomic/version/Version.vue";
import ServiceReleazio from "@/services/v2/service-releazio/ServiceReleazio";
import RouterService from "@/services/RouterService";

const currentVersion = import.meta.env.VITE_VERSION ?? "1.0.0";
const isCordova = computed(() => {
    return getDevice().cordova;
})
export type ErrorMessage = {
    message: string
}

const serviceRouter = AppController.of().service<RouterService>("router");
const serviceRealizio: ServiceReleazio = AppController.of().service<ServiceReleazio>("releazio");
const languagesPopup = ref<typeof LanguagesPopup>();
const {t} = useI18n({useScope: 'global'});
const controller = LoginController.getInstance();
// const error: Ref<ErrorMessage | null> = ref(null);
let headline = reactive({
    title: t("g.login.step1.headline.title"),
    text: t("g.login.step1.headline.text"),
});
const submitState = ref({
    loading: false,
    disable: true
});
const versionRef = ref(null);
let versionTimerRef: any = null;

const tel = controller.result.value.formatInternational ? controller.result.value.formatInternational : '';
const result = controller.result;
const error = ref(false)
const PhoneNumberInputRef = ref(null)
const self = ref(null)
let lang = computed(() => LangService.getInstance().get());
let captchaId: string | null = null;

onMounted(() => {
    initVersionEvents();
    // StatusBarService.toDarkContentAuth();
    // myUndefinedFunctionTest1();
});

function toReleazioPostsPage() {
    if (serviceRouter) {
        serviceRouter.toReleazioPostsPage();
    }
}

function openReleazioDeeplink() {
    if (serviceRealizio) {
        serviceRealizio.openDeepLink();
    }
}

function initVersionEvents() {
    if (versionRef.value && serviceRealizio) {
        const serviceRouter = AppController.of().service<RouterService>("router");
        serviceRealizio.initVersionEvents((versionRef.value as any).self, versionTimerRef, serviceRouter);
    }
}

onUnmounted(() => {
    controller.captchaController?.deleteCaptchaId(CaptchaPlace.LOGIN_PHONE_PAGE);
});

const techController = ViewTechController.of();

// TODO: fix this
const updatePhoneValue = (value: Event) => {
    try {
        if (f7.views.current.router.currentRoute.path === '/auth/login') {
            // @ts-ignore
            controller.result.value = value;
        }
    } catch (e: any) {
    }
}

function openLanguagePopup() {
    // @ts-ignore
    languagesPopup.value.popupState.opened = true;
}

function close() {
    headline.title = t("g.login.step1.headline.title");
    headline.text = t("g.login.step1.headline.text");
}

function detectCountryName(systemName: string) {
    switch (systemName) {
        case 'ru':
            return t("g.settings.selectLanguage.languages.ruShort");
        case 'en':
            return t("g.settings.selectLanguage.languages.enShort");
    }
    return systemName;
}

function openAlertToTelegram() {
    f7.dialog.create({
        title: t("g.alerts.to-telegram.title"),
        text: t("g.alerts.to-telegram.text"),
        cssClass: "simple",
        buttons: [
            {
                text: t("g.no")
            },
            {
                text: t("g.yes"),
                onClick: async () => {
                    const url = "https://t.me/BroMoney_robot";
                    if (getDevice().cordova) {
                        // ts-ignore
                        window.cordova.InAppBrowser.open(url, '_system');
                    } else {
                        try {
                            if (getDevice().ios) {
                                // @ts-ignore
                                window.webkit.messageHandlers.iosListener.postMessage(JSON.stringify({
                                    "deeplink": "https://t.me/BroMoney_robot"
                                }));
                            } else if (getDevice().android) {
                                // @ts-ignore
                                window.webInterface.onMessage(JSON.stringify({
                                    "deeplink": "https://t.me/BroMoney_robot"
                                }));
                            } else {
                                window.open(url, "_target");
                            }
                        } catch (e) {
                            window.open('https://t.me/BroMoney_robot', "_target");
                        }
                    }
                }
            }
        ]
    }).open();
}

const checkTelegram = (channel: Channel) => {
    if (channel.type === 'telegram' && !channel.isActive) {
        if (typeof f7.views.current.routes.find((r) => r.name === 'detect-telegram') === 'undefined') {
            f7.views.current.routes.push({
                name: 'detect-telegram',
                path: '/auth/login/detect-telegram',
                component: TelegramPage,
            });
        }
        f7.views.current.router.navigate('/auth/login/detect-telegram');
        return false;
    }
    return true;
}

const handleClickPhoneForm = async () => {
    submitState.value.loading = true;

    try {
        if (!checkPhoneValid()) {
            return;
        }

        setControllerPhoneValue();

        await controller.requestCheckExistsPhoneAndSetFlow();

        if (controller.flow === 'to_code') {
            await controller.generateCaptchaIfEnabled(
                CaptchaActions.CREATE,
                '#login-page_cf-turnstile',
                CaptchaPlace.LOGIN_PHONE_PAGE,
            );

            await controller.requestCreateSession();
            const firstActiveChannel = controller.getFirstActiveChannel()
            if (!checkTelegram(firstActiveChannel)) {
                return
            }

            await controller.generateCaptchaIfEnabled(
                CaptchaActions.SEND,
                '#login-page_cf-turnstile',
                CaptchaPlace.LOGIN_PHONE_PAGE,
            )

            await controller.requestManuallySendCode(firstActiveChannel.type);
            toEnterCode()
        } else {
            toEnterPassword();
        }
    } catch (e: any) {
        console.log(e)
        Sentry.captureMessage("Auth went wrong: " + e.message);
        Sentry.captureException(e);
        f7.dialog.alert(t('errors.70001'), 'Упс');
    } finally {
        submitState.value.loading = false;
    }

    function checkPhoneValid(): boolean {
        if (!controller.result.value.isValid) {
            error.value = true;
            return false;
        }
        return true;
    }

    function setControllerPhoneValue(): void {
        controller.phone = controller.getNumberPhone();
    }
}


function toEnterPassword() {
    if (typeof f7.views.current.routes.find((r) => r.name === 'login-password') === 'undefined') {
        f7.views.current.routes.push({
            name: 'login-password',
            path: '/auth/login/password',
            component: LoginEnterPasswordPage,
        });
    }
    f7.views.current.router.navigate('/auth/login/password');
}

function toEnterCode() {
    if (typeof f7.views.current.routes.find((route: any) => route.name === 'login-code') === 'undefined') {
        f7.views.current.routes.push({
            name: 'login-code',
            path: '/auth/login/code',
            component: LoginEnterCodePage,
        });
    }
    f7.views.current.router.navigate('/auth/login/code');
}

async function PAGE_BEFORE_IN() {
    if (serviceRealizio) {
        serviceRealizio.updateViewPost();
    }
}

async function PAGE_AFTER_IN() {
    await nextTick(() => {
        controller.captchaController?.deleteCaptchaId(CaptchaPlace.LOGIN_CODE_PAGE);
        controller.captchaController?.deleteCaptchaId(CaptchaPlace.LOGIN_CHANNEL_PAGE);
        controller.captchaController?.deleteCaptchaId(CaptchaPlace.LOGIN_PASSWORD_PAGE);
        // @ts-ignore
        // this.$refs['input-phone'].focus();
    });
}

// onUnmounted(() => {
//     controller.destroy();
// });
</script>

<style lang="scss">
.login-screen-navbar {
    --f7-navbar-inner-padding-left: 16px;
    --f7-navbar-inner-padding-right: 16px;

    .navbar-bg {
        &:after {
            content: none;
        }
    }

    .title {
        display: flex;
        align-items: center;
    }

    .left {
        .text-wrapper {
            display: flex;
            align-items: center;
            @include text(14px, 500, var(--blue1), 16.8px);
        }

        .i-small-arrow-down {
            width: 20px;
            height: 20px;
            background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.12055 12.3991L6.41221 9.69075C6.08235 9.36089 6.00874 8.98346 6.19138 8.55846C6.37402 8.13346 6.69937 7.92062 7.16742 7.91992H12.532C13.0008 7.91992 13.3264 8.13277 13.5091 8.55846C13.6917 8.98416 13.6178 9.36159 13.2872 9.69075L10.5789 12.3991C10.4747 12.5033 10.3619 12.5814 10.2403 12.6335C10.1188 12.6855 9.9886 12.7116 9.84971 12.7116C9.71082 12.7116 9.58062 12.6855 9.45909 12.6335C9.33756 12.5814 9.22471 12.5033 9.12055 12.3991Z' fill='%23007AFF'/%3E%3C/svg%3E%0A");
        }
    }

    .right {
    }
}

.login-screen-page {
    display: block;

    .page-content {
        height: 100%;

        &::-webkit-scrollbar {
            display: none;
        }

        //#cf-turnstile {
        //    display: none;
        //}

        .version-wrapper {
            .version_post-exists {
                //background: #efeff4;
            }
        }

        .login-screen-page_bottom-block {
            margin: 0 16px;
            display: flex;
            justify-content: center;

            &:before {
                content: none;
            }

            .version-wrapper {
                .version {
                    @include text(14px, 500, var(--grey3), 14px);
                }
            }

            .left-side {
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;

                .grey-telegram-svg {
                    width: 20px;
                    height: 20px;
                    background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M18.5439 2.30378C18.9685 2.66361 19.0442 3.22733 18.9397 3.74358L16.4627 15.9741C16.354 16.5107 16.0485 16.9812 15.537 17.1713C15.0254 17.3613 14.4868 17.2043 14.0541 16.8688L14.0456 16.8622L11.247 14.6085L9.34701 16.3579C8.93854 16.734 8.41334 16.9267 7.888 16.7953C7.36264 16.6639 6.99006 16.2466 6.80678 15.7224L5.12174 10.9038L2.01723 10.0155C2.01173 10.0139 2.00625 10.0122 2.00079 10.0105C1.76546 9.93564 1.54233 9.83278 1.36541 9.68814C1.18849 9.54351 1.00695 9.31117 0.997908 8.99301C0.988865 8.67482 1.15694 8.43255 1.3254 8.2781C1.49384 8.12366 1.7108 8.00834 1.94152 7.92027L17.0587 2.14957C17.5508 1.96177 18.1193 1.94395 18.5439 2.30378ZM6.29602 10.7117L7.63784 14.5488L8.31389 11.8107C8.33804 11.7129 8.38701 11.6229 8.45605 11.5496L11.9751 7.8103L6.29602 10.7117ZM8.6281 15.4269L10.3256 13.864L9.23151 12.983L8.6281 15.4269ZM9.74392 11.891L15.1442 6.15286C15.3395 5.94533 15.3572 5.62738 15.1861 5.39948C15.015 5.17159 14.7047 5.09985 14.451 5.2295L5.51163 9.79645L2.54002 8.94615L17.4766 3.24441C17.6748 3.16874 17.7622 3.18985 17.7854 3.1989C17.7981 3.22029 17.8332 3.30299 17.7911 3.51096L15.3142 15.7415C15.26 16.0091 15.1521 16.0641 15.1289 16.0727C15.1062 16.0812 14.9934 16.1088 14.7868 15.9539C14.776 15.9437 14.7647 15.9339 14.753 15.9244L9.74392 11.891ZM17.792 3.20202L17.791 3.20143L17.792 3.20202ZM17.782 3.19378L17.7812 3.19291L17.782 3.19378Z' fill='%23ACB4BE'/%3E%3C/svg%3E%0A");
                }
            }

            .right-side {
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;

                .grey-world-svg {
                    width: 20px;
                    height: 20px;
                    background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18.3332 10.0049C18.3221 14.6602 14.5532 18.3046 10.057 18.3332C5.43655 18.364 1.62667 14.6068 1.66682 9.92589C1.70568 5.38118 5.38915 1.66589 10.0011 1.66675C14.6335 1.66846 18.3319 5.41834 18.3332 10.0049ZM7.84736 13.4943C7.87383 13.4943 7.89476 13.4943 7.91526 13.4943C8.38503 13.4567 8.85737 13.405 9.32971 13.3841C10.2434 13.3444 11.1588 13.3811 12.0664 13.4938C12.1428 13.5032 12.1616 13.4819 12.1766 13.4114C12.376 12.4718 12.4717 11.5228 12.5114 10.5657C12.5152 10.4696 12.4776 10.4559 12.3948 10.4559C10.797 10.4576 9.19931 10.4576 7.60179 10.4559C7.5672 10.4559 7.53261 10.4593 7.50015 10.4615C7.44933 10.9274 7.67482 12.8933 7.84736 13.4943ZM12.493 9.31813C12.4963 9.30222 12.4985 9.28609 12.4994 9.26987C12.4677 8.36353 12.359 7.46154 12.1744 6.57363C12.1586 6.49761 12.1198 6.50102 12.0604 6.507C11.7029 6.5416 11.3459 6.58302 10.9876 6.6031C10.0818 6.65758 9.17313 6.63787 8.27058 6.54416C8.1305 6.52921 7.99 6.51554 7.85205 6.50145C7.69959 6.96314 7.44805 9.05035 7.51253 9.31813H12.493ZM2.82161 10.4568C2.82027 10.4808 2.82027 10.5049 2.82161 10.5289C2.86987 10.8625 2.90019 11.2003 2.97023 11.5296C3.19056 12.5661 3.64161 13.5397 4.28987 14.3779C4.32874 14.4292 4.3565 14.4492 4.42696 14.4228C4.90528 14.243 5.38274 14.061 5.86704 13.8996C6.13823 13.809 6.42223 13.7561 6.70537 13.686C6.70523 13.6613 6.70352 13.6366 6.70025 13.6121C6.49625 12.6152 6.38318 11.6018 6.36244 10.5845C6.3603 10.4811 6.32827 10.4542 6.22706 10.4563C5.13462 10.4598 4.04217 10.4585 2.95016 10.4585L2.82161 10.4568ZM13.6457 10.4649C13.6224 11.5485 13.5018 12.6278 13.2852 13.6899C14.1086 13.8658 14.8957 14.099 15.6508 14.4539C16.5638 13.2769 17.0755 11.9554 17.1801 10.4649H13.6457ZM6.7105 6.31823C6.69538 6.31161 6.67983 6.30604 6.66395 6.30157C5.88796 6.13073 5.13035 5.90864 4.40689 5.57551C4.38869 5.57213 4.36993 5.57328 4.35228 5.57887C4.33462 5.58445 4.31862 5.5943 4.30568 5.60754C4.19934 5.74507 4.09598 5.88558 3.99947 6.03036C3.48801 6.79862 3.13022 7.65868 2.94589 8.56303C2.89507 8.80861 2.86731 9.05889 2.82802 9.31301C2.87072 9.31728 2.89379 9.32198 2.91813 9.32198C4.03491 9.32198 5.1517 9.32198 6.26848 9.32411C6.35731 9.32411 6.36287 9.28525 6.36543 9.2152C6.39293 8.41577 6.4767 7.61926 6.61612 6.83159C6.64601 6.66118 6.67847 6.4912 6.7105 6.31823ZM13.6551 9.31472H17.1724C17.1348 9.07725 17.1058 8.84876 17.0618 8.62282C16.852 7.53353 16.3909 6.50822 15.7152 5.62847C15.6683 5.56611 15.6298 5.55373 15.5585 5.58832C15.0486 5.82242 14.5186 6.00996 13.9749 6.14867C13.7494 6.20846 13.5218 6.25971 13.3027 6.31267C13.3787 6.81536 13.4667 7.30866 13.5248 7.80494C13.5829 8.30122 13.6124 8.80818 13.6551 9.31472ZM11.8507 5.37819C11.8474 5.35776 11.8429 5.33752 11.8375 5.31755C11.6893 4.84588 11.495 4.38996 11.2575 3.9564C11.0474 3.58312 10.8125 3.22735 10.4563 2.97152C10.148 2.74986 9.85842 2.74815 9.54965 2.96682C9.32624 3.1323 9.13405 3.33621 8.98208 3.56902C8.61394 4.10417 8.36966 4.69826 8.16424 5.31029C8.15832 5.33341 8.15432 5.35698 8.15228 5.38076C8.77879 5.5328 11.2579 5.53152 11.8507 5.37819ZM11.8755 14.6141C10.6373 14.4688 9.38637 14.4681 8.14801 14.612C8.14942 14.6302 8.15213 14.6483 8.15613 14.6662C8.32909 15.1937 8.53579 15.707 8.82235 16.1841C9.02308 16.5177 9.24643 16.8346 9.57912 17.0528C9.7397 17.1583 9.91095 17.2288 10.1112 17.1861C10.3521 17.1348 10.534 16.99 10.6993 16.8205C11.047 16.4643 11.287 16.0367 11.4864 15.587C11.6252 15.2735 11.7435 14.9506 11.8755 14.6141ZM5.18373 15.347C5.96163 16.0498 6.88389 16.5738 7.88579 16.8824C7.71496 16.5407 7.53858 16.2076 7.38911 15.8633C7.23964 15.5191 7.11963 15.1629 6.98596 14.8106C6.69512 14.8251 5.44552 15.1851 5.18373 15.347ZM7.00005 5.20052C7.22213 4.47446 7.50356 3.78214 7.91697 3.11289C6.90313 3.42147 5.96987 3.94996 5.18373 4.66068C5.47371 4.81742 6.60587 5.15269 7.00005 5.19881V5.20052ZM13.0166 5.20522C13.6282 5.07709 14.2308 4.91437 14.8244 4.66537C14.0444 3.95918 13.1189 3.43273 12.1133 3.12314C12.2842 3.46823 12.4614 3.79666 12.6092 4.13791C12.7569 4.47916 12.8782 4.83792 13.0166 5.20351V5.20522ZM14.8252 15.3376C14.7625 15.312 14.7206 15.2949 14.6783 15.2778C14.1734 15.0876 13.6551 14.935 13.1276 14.8212C13.0204 14.7973 12.9884 14.8242 12.9568 14.9216C12.7889 15.4829 12.5636 16.0255 12.2846 16.5407C12.2244 16.6509 12.1603 16.759 12.0847 16.8914C13.1012 16.58 14.0367 16.049 14.8252 15.3359V15.3376Z' fill='%23ACB4BE'/%3E%3C/svg%3E%0A");
                }

                .arrow-down-svg {
                    margin-left: 4px;
                    width: 20px;
                    height: 20px;
                    background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.12055 12.3991L6.41221 9.69075C6.08235 9.36089 6.00874 8.98346 6.19138 8.55846C6.37402 8.13346 6.69937 7.92062 7.16742 7.91992H12.532C13.0008 7.91992 13.3264 8.13277 13.5091 8.55846C13.6917 8.98416 13.6178 9.36159 13.2872 9.69075L10.5789 12.3991C10.4747 12.5033 10.3619 12.5814 10.2403 12.6335C10.1188 12.6855 9.9886 12.7116 9.84971 12.7116C9.71082 12.7116 9.58062 12.6855 9.45909 12.6335C9.33756 12.5814 9.22471 12.5033 9.12055 12.3991Z' fill='%23D5D7D8'/%3E%3C/svg%3E%0A");
                }
            }

            .text-wrapper {
                display: flex;
                align-items: center;
                text-align: center;
                cursor: pointer;
                @include text(14px, 500, #ACB4BE, 16.8px);
            }
        }
    }

    .block {
        margin: 0;
        padding: 0 0 16px 0;

        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .phone-form {
            padding: 0 24px;

            & + #login-page_cf-turnstile {
                margin-top: 24px;
            }

            .phone-input-wrapper {
                margin: 0;
                display: flex;
                justify-content: flex-start;
                @include text(20px, 400, var(--grey1), 24px);
                position: relative;

                &::after {
                    content: '';
                    position: absolute;
                    background-color: var(--grey4);
                    display: block;
                    z-index: 15;
                    top: auto;
                    right: 0;
                    bottom: 0;
                    left: auto;
                    height: 1px;
                    width: 100%;
                    transform-origin: 50% 100%;
                    transform: scaleY(calc(1 / var(--f7-device-pixel-ratio)));
                }

                .phone-code {
                    position: relative;
                    padding: 12px 16px 12px;

                    &::after {
                        content: '';
                        position: absolute;
                        background-color: var(--grey4);
                        display: block;
                        z-index: 15;
                        top: 7px;
                        right: 0px;
                        bottom: auto;
                        left: auto;
                        height: 32px;
                        width: 1px;
                        transform-origin: 100% 50%;
                        transform: scaleX(calc(1 / var(--f7-device-pixel-ratio)));
                    }
                }

                .input-phone {
                    //width: calc(100% - 32px);
                    line-height: 20px;
                    padding: 12px 16px;

                    &::placeholder {
                        opacity: 0.3;
                    }
                }
            }

            .error-block {
                margin-top: 8px;
                height: 12px;

                .error-message {
                    text-align: center;
                    @include text(12px, 400, var(--red), 14px);
                }
            }

            .selected-channel-row {
                margin-top: 20px;

                .title {
                    @include text(15px, 400, var(--grey1), 20.4px);
                    margin-bottom: 8px;
                    font-weight: 500;
                }

                .selected-channel__wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;

                    &:after {
                        content: '';
                        position: absolute;
                        background-color: var(--f7-list-border-color);
                        display: block;
                        z-index: 15;
                        top: auto;
                        right: auto;
                        bottom: 0;
                        left: 0;
                        height: 1px;
                        width: 100%;
                        transform-origin: 50% 100%;
                        transform: scaleY(calc(1 / var(--f7-device-pixel-ratio)));
                    }

                    .i-phone-number-select {
                        width: 20px;
                        height: 20px;
                        background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.12055 12.3991L6.41221 9.69075C6.08235 9.36089 6.00874 8.98346 6.19138 8.55846C6.37402 8.13346 6.69937 7.92062 7.16742 7.91992H12.532C13.0008 7.91992 13.3264 8.13277 13.5091 8.55846C13.6917 8.98416 13.6178 9.36159 13.2872 9.69075L10.5789 12.3991C10.4747 12.5033 10.3619 12.5814 10.2403 12.6335C10.1188 12.6855 9.9886 12.7116 9.84971 12.7116C9.71082 12.7116 9.58062 12.6855 9.45909 12.6335C9.33756 12.5814 9.22471 12.5033 9.12055 12.3991Z' fill='%239E9FA9'/%3E%3C/svg%3E%0A");
                    }

                    .selected-channel {
                        cursor: pointer;
                        padding: 0px 8px;
                        width: 100%;
                        background-color: var(--white2);
                        height: 45px;
                        @include text(15px, 400, var(--grey1), 18px);

                        &::placeholder {
                            @include text(15px, 400, var(--grey4), 18px);
                        }
                    }
                }
            }


            .button {
                margin-top: 26px;
            }

            &.error {
                .phone-input-wrapper {
                    &::after,
                    &::before {
                        background-color: var(--red);
                    }
                }
            }
        }
    }
}
</style>
